import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import styles from './PortfolioCreatePage.module.css';
import RoleOnlyModal from '../../shared/components/RoleOnlyModal';
import EditBasicInfoSection from '../Talent/Profile/components/EditBasicInfoSection';
import EditContactSection from '../Talent/Profile/components/EditContactSection';
import EditFilmographySection from '../Talent/Profile/components/EditFilmographySection';
import { createTalentProfile, updateTalentProfile, uploadFile, addTalentMedia } from '../../api';

const GENDER_TO_API = { 남성: 'MALE', 여성: 'FEMALE', 기타: 'OTHER' };
const VISIBILITY_TO_API = { public: 'PUBLIC', link: 'LINK_ONLY', private: 'PRIVATE' };

const STEPS = ['기본 정보', '포트폴리오', '필모그래피'];

/* ── 스텝 인디케이터 ── */
function StepIndicator({ current }) {
  return (
    <div className={styles.stepIndicator}>
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={i} className={styles.stepItem}>
            <div className={styles.stepLabelGroup}>
              {/* 번호 원 */}
              <div className={
                active ? styles.stepCircle + ' ' + styles.stepCircleActive
                : done  ? styles.stepCircle + ' ' + styles.stepCircleDone
                :          styles.stepCircle + ' ' + styles.stepCircleDefault
              }>
                {i + 1}
              </div>
              <span className={active ? styles.stepLabelActive : styles.stepLabel}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={styles.stepDivider} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── 사진 포트폴리오 섹션 ── */
function PhotoPortfolioSection({ photos, setPhotos }) {
  const fileRef = useRef(null);

  function handleFiles(files) {
    const newPhotos = Array.from(files).map(f => ({
      id: Date.now() + Math.random(),
      url: URL.createObjectURL(f),
      file: f,
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  }

  function removePhoto(id) {
    setPhotos(prev => prev.filter(p => p.id !== id));
  }

  const slots = [...photos, ...Array(Math.max(0, 4 - photos.length)).fill(null)];

  return (
    <div className={styles.photoSection}>
      <div>
        <h2 className={styles.photoSectionTitle}>사진 포트폴리오</h2>
        <p className={styles.photoSectionSub}>프로필 사진과 포트폴리오 이미지를 추가하세요</p>
      </div>

      <div className={styles.photoCard}>
        <input
          ref={fileRef} type="file" accept="image/*" multiple
          className={styles.photoHiddenInput}
          onChange={e => handleFiles(e.target.files)}
        />

        {/* 사진 그리드 */}
        <div className={styles.photoGrid}>
          {slots.map((photo, i) =>
            photo ? (
              <div key={photo.id} className={styles.photoThumb}>
                <img src={photo.url} alt="" className={styles.photoThumbImg} />
                <button
                  onClick={() => removePhoto(photo.id)}
                  className={styles.photoRemoveBtn}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M1 1l10 10M11 1L1 11" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div
                key={`empty-${i}`}
                onClick={() => fileRef.current?.click()}
                className={styles.photoEmptySlot}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="#A9A9B2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className={styles.photoEmptyLabel}>프로필 사진을 첨부하세요.</span>
              </div>
            )
          )}
        </div>

        <p className={styles.photoHint}>최소 3장 이상의 사진을 추가하는 것을 권장합니다.</p>
      </div>
    </div>
  );
}

/* ── 메인 페이지 ── */
export default function PortfolioCreatePage() {
  const navigate = useNavigate();
  const { isLoggedIn, role, isTalent, isProducer, switchRole } = useAuth();

  const [step, setStep] = useState(0);
  const [createdId, setCreatedId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showActorOnlyModal, setShowActorOnlyModal] = useState(false);

  // 기본 정보
  const [name, setName] = useState('');
  const [stage, setStage] = useState('');
  const [gender, setGender] = useState('');
  const [birth, setBirth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [agency, setAgency] = useState('');
  const [customSkill, setCustomSkill] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);

  // 연락처
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [instagram, setInstagram] = useState('');
  const [youtube, setYoutube] = useState('');

  // 사진
  const [photos, setPhotos] = useState([]);

  /* 다음 단계: 기본정보 → 포트폴리오 (프로필 생성) */
  const handleNextFromStep0 = async () => {
    if (!name || !gender || !birth) {
      alert('이름, 성별, 생년월일은 필수 항목입니다.');
      return;
    }
    if (isSaving) return;
    setIsSaving(true);
    try {
      const created = await createTalentProfile({
        name,
        gender: GENDER_TO_API[gender],
        birthDate: birth,
        stageName: stage || undefined,
        heightCm: parseInt(height) || undefined,
        weightKg: parseInt(weight) || undefined,
      });
      setCreatedId(created.id);

      // 나머지 정보 업데이트
      await updateTalentProfile({
        id: created.id,
        name,
        stageName: stage || undefined,
        gender: GENDER_TO_API[gender],
        birthDate: birth,
        heightCm: parseInt(height) || undefined,
        weightKg: parseInt(weight) || undefined,
        bio: bio || undefined,
        phone: phone || undefined,
        residence: location || undefined,
        contactEmail: email || undefined,
        agency: agency || undefined,
        instagramUrl: instagram || undefined,
        youtubeUrl: youtube || undefined,
        visibility: VISIBILITY_TO_API['public'],
      });

      setStep(1);
    } catch {
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  /* 스텝 1 → 2: 사진 업로드 후 이동 */
  const handleNextFromStep1 = async () => {
    if (isSaving) return;
    const photosWithFile = photos.filter(p => p.file);
    if (photosWithFile.length === 0) { setStep(2); return; }
    setIsSaving(true);
    try {
      const uploaded = await Promise.all(
        photosWithFile.map(async ({ file }) => {
          const { fileEntityId, url } = await uploadFile(file, 'IMAGE');
          await addTalentMedia({ talentProfileId: createdId, fileEntityId, mediaType: 'IMAGE', photoType: 'PORTFOLIO' });
          return url;
        })
      );
      // blob URL을 서버 URL로 교체
      setPhotos(prev => {
        let uploadedIdx = 0;
        return prev.map(p => p.file ? { ...p, url: uploaded[uploadedIdx++], file: null } : p);
      });
    } catch {
      alert('사진 업로드에 실패했습니다. 다시 시도해주세요.');
      return;
    } finally {
      setIsSaving(false);
    }
    setStep(2);
  };

  /* 최종 완료: 필모그래피 저장 후 프로필로 이동 */
  const handleFinish = () => {
    navigate('/talent/profile');
  };

  return (
  <PageLayout>
    <div className={styles.pageWrapper}>
      {(!isLoggedIn || role !== 'ACTOR') ? (
        showActorOnlyModal && (
          <RoleOnlyModal
            title="배우 전용 기능입니다"
            message={isLoggedIn ? '포트폴리오 만들기는 배우 계정에서만 이용할 수 있습니다.' : '배우로 로그인해서 이용해주세요.'}
            onClose={() => { setShowActorOnlyModal(false); navigate('/talents'); }}
            hasBothRoles={isLoggedIn && isTalent && isProducer}
            onSwitch={() => { switchRole('TALENT'); window.location.href = '/talent/dashboard'; }}
            switchLabel="배우로 계정 전환하기"
            onboardingPath={isLoggedIn && !isTalent ? '/onboarding/talent' : undefined}
            onboardingLabel="배우 계정 만들러 가기"
          />
        )
      ) : (
        <>
          <div className={styles.backArea}>
            <button
              onClick={() => navigate('/talents')}
              className={styles.backBtn}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 12H5M5 12l7 7M5 12l7-7"
                  stroke="#A2A2A2"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className={styles.backBtnLabel}>배우 목록으로</span>
            </button>
          </div>

          <div className={styles.header}>
            <div>
              <h1 className={styles.headerTitle}>포트폴리오 만들기</h1>
              <p className={styles.headerSub}>
                프로필 정보와 작품을 추가하여 포트폴리오를 완성하세요.
              </p>
            </div>
            <StepIndicator current={step} />
          </div>

          {step === 0 && (
            <div className={styles.stepContent}>
              <EditBasicInfoSection
                name={name} setName={setName}
                stage={stage} setStage={setStage}
                gender={gender} setGender={setGender}
                birth={birth} setBirth={setBirth}
                height={height} setHeight={setHeight}
                weight={weight} setWeight={setWeight}
                location={location} setLocation={setLocation}
                bio={bio} setBio={setBio}
                agency={agency} setAgency={setAgency}
                selectedSkills={selectedSkills} setSelectedSkills={setSelectedSkills}
                customSkill={customSkill} setCustomSkill={setCustomSkill}
              />
              <EditContactSection
                email={email} setEmail={setEmail}
                phone={phone} setPhone={setPhone}
                instagram={instagram} setInstagram={setInstagram}
                youtube={youtube} setYoutube={setYoutube}
              />
              <div className={styles.btnRowCenter}>
                <button
                  onClick={handleNextFromStep0}
                  disabled={isSaving}
                  className={isSaving ? styles.btnNextDisabled : styles.btnNext}
                >
                  {isSaving ? '저장 중...' : '다음 단계 : 포트폴리오'}
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className={styles.stepContent}>
              <PhotoPortfolioSection photos={photos} setPhotos={setPhotos} />
              <div className={styles.btnRow}>
                <button
                  onClick={() => setStep(0)}
                  className={styles.btnSecondary}
                >
                  이전 단계로 돌아가기
                </button>
                <button
                  onClick={handleNextFromStep1}
                  disabled={isSaving}
                  className={isSaving ? styles.btnNextDisabled : styles.btnPrimary}
                >
                  {isSaving ? '업로드 중...' : '다음 단계 : 필모그래피'}
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className={styles.stepContent}>
              <EditFilmographySection talentProfileId={createdId} />
              <div className={styles.btnRow}>
                <button
                  onClick={() => setStep(1)}
                  className={styles.btnSecondary}
                >
                  이전 단계로 돌아가기
                </button>
                <button
                  onClick={handleFinish}
                  className={styles.btnPrimary}
                >
                  포트폴리오 생성 완료
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </PageLayout>
);}
